import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, Save, ExternalLink, EyeOff } from "lucide-react";
import { MBTI_GROUPS } from "@/lib/mbti";

const MBTI_CODES = MBTI_GROUPS.flatMap((g) => g.types.map((t) => ({ ...t, club: g.club })));
const STATUS = [
  { v: "competing", label: "A coopetir" },
  { v: "observer", label: "Observador" },
];
const CLUB_ROLES = [
  { v: "none", label: "Sem cargo" },
  { v: "president", label: "Consultor Presidente da Liga" },
  { v: "coach", label: "Secretário do Clube" },
  { v: "administrator", label: "Presidente do Clube" },
];

/* Edição rápida do cartão do consultor — só para o admin da aplicação. */
export default function PersonEditDialog({ person, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: person.name || "", email: person.email || "", phone: person.phone || "",
    mbti: person.coach_mbti || "", team_id: person.team_id || "",
    role_in_team: person.role_in_team || "consultant",
    league_status: person.league_status || "observer",
    club_role: person.club_role || "none",
  });
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(!!person.hidden);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/players/${person.id}`, form);
      if (hidden !== !!person.hidden) {
        await api.patch("/directory/visibility", {
          items: [{ kind: "player", id: person.id }], hidden,
        });
      }
      toast.success("Cartão actualizado");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return createPortal((
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="person-edit-dialog">
      <div className="w-full max-w-xl kw-card p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-5 border-b border-[#222] pb-4">
          <div>
            <div className="kw-overline">Editar cartão · admin</div>
            <h2 className="font-display text-2xl font-black uppercase">{person.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="person-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="kw-overline block mb-2">Nome</label>
            <input className="kw-input" value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="person-edit-name" />
          </div>
          <div>
            <label className="kw-overline block mb-2">Email</label>
            <input className="kw-input" value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="person-edit-email" />
          </div>
          <div>
            <label className="kw-overline block mb-2">Telefone</label>
            <input className="kw-input" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              data-testid="person-edit-phone" />
          </div>
          <div>
            <label className="kw-overline block mb-2">MBTI · define o clube</label>
            <select className="kw-input" value={form.mbti} onChange={(e) => set("mbti", e.target.value)}
              data-testid="person-edit-mbti">
              <option value="">Sem MBTI</option>
              {MBTI_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.code} · {c.label} ({c.club})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="kw-overline block mb-2">Equipa</label>
            <select className="kw-input" value={form.team_id} onChange={(e) => set("team_id", e.target.value)}
              data-testid="person-edit-team">
              <option value="">Sem equipa</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="kw-overline block mb-2">Função na equipa</label>
            <select className="kw-input" value={form.role_in_team} onChange={(e) => set("role_in_team", e.target.value)}
              data-testid="person-edit-role-in-team">
              <option value="consultant">Consultor</option>
              <option value="assistant">Assistente</option>
            </select>
          </div>
          <div>
            <label className="kw-overline block mb-2">Estado na liga</label>
            <select className="kw-input" value={form.league_status} onChange={(e) => set("league_status", e.target.value)}
              data-testid="person-edit-status">
              {STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="kw-overline block mb-2">Cargo</label>
            <select className="kw-input" value={form.club_role} onChange={(e) => set("club_role", e.target.value)}
              data-testid="person-edit-club-role">
              {CLUB_ROLES.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
            </select>
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
              Quem tem cargo não coopete — passa automaticamente a Observador
            </div>
          </div>
          <div className="sm:col-span-2 border border-[#222] p-3 flex items-start gap-3">
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)}
              className="mt-0.5 accent-[#E50000]" id="hide-from-directory"
              data-testid="person-edit-hidden" />
            <label htmlFor="hide-from-directory" className="cursor-pointer">
              <span className="kw-overline inline-flex items-center gap-2"><EyeOff className="w-3 h-3" /> Ocultar do diretório</span>
              <span className="block text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                Desaparece do Diretório, da pesquisa e das páginas públicas — admin e staff continuam a ver
              </span>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-[#222]">
          <a href={`/players/${person.id}`} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-white inline-flex items-center gap-1"
            data-testid="person-edit-full-profile">
            Perfil completo <ExternalLink className="w-3 h-3" />
          </a>
          <div className="flex gap-3">
            <button className="kw-btn-secondary !py-2" onClick={onClose}>Cancelar</button>
            <button className="kw-btn-primary !py-2 inline-flex items-center gap-2" onClick={save} disabled={saving}
              data-testid="person-edit-save">
              <Save className="w-4 h-4" /> {saving ? "A gravar…" : "Gravar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
